<template>
  <div
    class="bg-white border border-gray-200 rounded shadow-sm transition-all duration-200"
    :class="isCollapsed ? 'p-3 space-y-3' : 'p-4 space-y-4'"
  >
    <div class="space-y-3">
      <div class="flex items-start justify-between gap-4">
        <router-link
          :to="{ name: 'EmployeeDashboard', params: { staffId: staff.id } }"
          class="block text-lg font-semibold text-black hover:text-cerulean transition-colors"
        >
          {{ staff.firstname }} {{ staff.surname }}
        </router-link>
        <div class="text-right">
          <div class="text-xs font-semibold uppercase text-gray-500">
            Worked hours
          </div>
          <div class="text-lg font-semibold tabular-nums text-gray-800">
            {{ formatCompactHours(rangeTotals.workedTotal) }}
          </div>
        </div>
      </div>
      <div
        class="grid gap-2 text-[11px]"
        :style="summaryMetricGridStyle"
      >
        <div class="min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <div class="font-semibold uppercase tracking-wide text-gray-500">
            Billable
          </div>
          <div class="mt-1 text-sm font-semibold text-gray-800">
            {{ rangeBillablePct }}%
          </div>
        </div>
        <div class="min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <div class="font-semibold uppercase tracking-wide text-gray-500">
            Hours vs target
          </div>
          <div class="mt-1 text-sm font-semibold text-gray-700">
            <span>{{ targetDeltaLabel(rangeTargetDelta) }}</span>
            <span
              v-if="!isEffectivelyZeroHours(rangeTargetDelta)"
              :class="targetDeltaClass(rangeTargetDelta)"
            >
              {{ formatCompactHours(Math.abs(rangeTargetDelta)) }}
            </span>
          </div>
        </div>
        <div
          v-if="rangeUnaccountedHours > 0"
          class="min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2"
        >
          <div class="font-semibold uppercase tracking-wide text-gray-500">
            Unaccounted
          </div>
          <div class="mt-1 text-sm font-semibold text-gray-800">
            {{ formatCompactHours(rangeUnaccountedHours) }}
          </div>
        </div>
        <div class="min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <div class="font-semibold uppercase tracking-wide text-gray-500">
            Days Below Target
          </div>
          <div class="mt-1 text-sm font-semibold text-gray-800">
            {{ exceptionCount }}
          </div>
        </div>
      </div>
    </div>

    <div class="space-y-10 border-t border-gray-100 pt-4 pb-3">
      <section
        v-for="section in monthSections"
        :key="`${staff.id}-${section.key}`"
        class="space-y-3 pt-3 first:pt-0"
      >
        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <div class="h-px flex-1 bg-gray-300" />
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-600">
              {{ section.label }}
            </h3>
            <div class="h-px flex-1 bg-gray-300" />
          </div>
          <div class="h-px w-full bg-gray-300" />
        </div>

        <div
          class="grid text-center text-xs text-gray-700 font-medium"
          :class="showWeeklyGauges ? 'grid-cols-8' : 'grid-cols-7'"
        >
          <div
            v-for="day in weekdays"
            :key="`${section.key}-${day}`"
          >
            {{ day }}
          </div>
          <div
            v-if="showWeeklyGauges"
            class="uppercase text-[10px] font-semibold text-gray-500"
          >
            Week
          </div>
        </div>

        <div
          class="grid gap-1"
          :class="showWeeklyGauges ? 'grid-cols-8' : 'grid-cols-7'"
        >
          <template
            v-for="(week, weekIdx) in section.weeks"
            :key="`week-${staff.id}-${section.key}-${weekIdx}`"
          >
            <div class="relative contents">
              <div
                v-for="(date, dayIdx) in week.cells"
                :key="`${staff.id}-${section.key}-${weekIdx}-${dayIdx}`"
                class="relative group aspect-square text-xs font-medium rounded border"
                :class="date ? cellClass(date) : 'border-gray-100 bg-gray-50'"
                :style="date ? cellStyle(date) : null"
              >
                <template v-if="date">
                  <div class="relative h-full w-full">
                    <span
                      class="absolute inset-0 flex items-center justify-center"
                      :class="isNonWorkingDay(date) ? 'text-gray-400' : 'text-gray-800'"
                    >
                      {{ new Date(date).getUTCDate() }}
                    </span>
                  </div>
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block">
                    <div class="bg-white text-sm p-2 rounded-lg shadow-lg whitespace-nowrap">
                      <div class="font-semibold mb-1">
                        {{ formatTooltipDate(date) }}
                      </div>
                      <div>{{ tooltipText(date) }}</div>
                    </div>
                  </div>
                </template>
                <div
                  v-if="date && isWeekLoading(week)"
                  class="absolute inset-0 z-20 flex items-center justify-center rounded bg-white/70"
                >
                  <div class="h-4 w-4 rounded-full border-2 border-cerulean border-t-transparent animate-spin" />
                </div>
              </div>
            </div>

            <div
              v-if="showWeeklyGauges"
              :key="`${staff.id}-${section.key}-${weekIdx}-gauge`"
              class="flex min-h-[4.5rem] flex-col justify-center rounded border border-gray-200 bg-gray-50 px-2 text-center text-[11px]"
              :class="isWeekLoading(week) ? 'opacity-50' : ''"
            >
              <div class="font-semibold tabular-nums text-gray-900">
                {{ formatCompactHours(weekSummary(week).worked) }}
              </div>
              <div
                class="mt-0.5 font-semibold"
                :class="targetDeltaClass(weekSummary(week).delta)"
              >
                {{ formatWeekTargetDelta(weekSummary(week).delta) }}
              </div>
              <div class="mt-0.5 text-gray-500">
                {{ weekSummary(week).billablePct }}% billable
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>

    <div class="daily-target-control rounded border border-gray-200 bg-gray-50 px-3 py-3">
      <div class="flex items-center gap-3">
        <div class="min-w-[8.5rem] text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Daily billable target
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          :value="targetPct"
          class="billable-target-slider flex-1"
          :style="targetSliderStyle"
          :aria-label="`${staff.firstname} ${staff.surname} billable target`"
          @input="$emit('update-target-pct', staff.id, Number($event.target.value))"
        >
        <div class="w-9 text-right text-sm font-semibold tabular-nums text-gray-800">
          {{ targetPct }}%
        </div>
      </div>
      <div class="mt-2 text-xs text-gray-500">
        Highlights worked days below this billable percentage.
      </div>
    </div>

    <div
      v-if="!isCollapsed"
      class="rounded border border-gray-100 bg-gray-50 p-3"
    >
      <div class="mb-2 text-xs font-semibold uppercase text-gray-500">
        Range mix
      </div>
      <BillableGauge
        :staff-id="staff.id"
        :include-client-non-billable="includeClientAsBillable"
        layout="horizontal"
      />
    </div>
    <div
      v-if="hasClientNonBillableHours"
      class="flex justify-end"
    >
      <label class="flex items-center gap-2 text-xs text-gray-600 font-semibold">
        <span>Combine billable and client non-billable</span>
        <input
          type="checkbox"
          class="sr-only"
          :checked="includeClientAsBillable"
          @change="$emit('update-include-client', staff.id, $event.target.checked)"
        >
        <span
          class="relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full border border-gray-300 transition-colors"
          :class="includeClientAsBillable ? 'bg-cerulean' : 'bg-gray-200'"
        >
          <span
            class="inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform"
            :class="includeClientAsBillable ? 'translate-x-4' : 'translate-x-0.5'"
          />
        </span>
      </label>
    </div>
    <button
      type="button"
      class="mt-2 flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-cerulean"
      :aria-label="isCollapsed ? 'Show range mix' : 'Hide range mix'"
      @click="$emit('toggle-gauge', staff.id)"
    >
      <ChevronDownIcon
        v-if="isCollapsed"
        class="h-4 w-4"
      />
      <ChevronUpIcon
        v-else
        class="h-4 w-4"
      />
      <span>{{ isCollapsed ? 'Show range mix' : 'Hide range mix' }}</span>
    </button>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUpdate, onMounted, onUpdated } from 'vue'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-vue-next'
import BillableGauge from '@/EmployeeInsightsComponents/BillableGauge.vue'
import {
  computeGaugeTotals,
  countWeekdaysInIsoDates,
  formatHoursToParts,
  formatTooltipDate,
  heatmapWorkedCellBackground,
  isPastIsoDate,
  isWeekendIsoDate,
  staffRangeTotals,
  statHours,
  statForDate,
  targetHoursForWeekdays,
  utcDateFromIso,
  weekLoadingKey
} from '@/utils/employeeInsightsCalculations'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'

const props = defineProps({
  staff: { type: Object, required: true },
  weeks: { type: Array, required: true },
  weekdays: { type: Array, required: true },
  showWeeklyGauges: { type: Boolean, required: true },
  dailyStatsByStaff: { type: Object, required: true },
  dailyStatsByStaffDateIndex: { type: Object, default: () => ({}) },
  loadingWeeks: { type: Object, default: () => ({}) },
  isCollapsed: { type: Boolean, default: false },
  includeClientAsBillable: { type: Boolean, default: false },
  targetPct: { type: Number, required: true }
})

defineEmits(['toggle-gauge', 'update-include-client', 'update-target-pct'])

const setupStartedAt = performance.now()
let updateStartedAt = null
const eiStore = useEmployeeInsightsStore()

const rangeTotals = computed(() => staffRangeTotals(props.dailyStatsByStaff, props.staff.id))
const hasClientNonBillableHours = computed(() => rangeTotals.value.clientNonBillable > 0)
const weeklyTarget = computed(() => Number(eiStore.weeklyHourTargets?.[props.staff.id] ?? eiStore.defaultWeeklyHourTarget ?? 40))
const rangeExpectedHours = computed(() => {
  const dates = props.weeks.flatMap(week => week.actualDates || [])
  return adjustedExpectedHoursForDates(dates)
})
const rangeGaugeTotals = computed(() =>
  computeGaugeTotals(
    props.dailyStatsByStaff?.[props.staff.id] || [],
    eiStore.startDate,
    eiStore.endDate,
    weeklyTarget.value
  )
)
const rangeBillablePct = computed(() => {
  const denom = rangeGaugeTotals.value.denom || 0
  if (!denom) return 0
  const billableHours = props.includeClientAsBillable
    ? rangeGaugeTotals.value.billable + rangeGaugeTotals.value.clientNonBillable
    : rangeGaugeTotals.value.billable
  return Math.round((billableHours / denom) * 100)
})
const rangeEnteredHours = computed(() => rangeTotals.value.workedTotal + rangeTotals.value.pto)
const rangeTargetDelta = computed(() => rangeEnteredHours.value - rangeExpectedHours.value)
const rangeUnaccountedHours = computed(() =>
  (props.dailyStatsByStaff?.[props.staff.id] || []).reduce((total, stat) => total + statHours(stat).unaccounted, 0)
)
const summaryMetricGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${rangeUnaccountedHours.value > 0 ? 4 : 3}, minmax(0, 1fr))`
}))
const targetSliderStyle = computed(() => ({
  background: `linear-gradient(to right, #12a9c8 0%, #12a9c8 ${props.targetPct}%, #d1d5db ${props.targetPct}%, #d1d5db 100%)`
}))
const exceptionCount = computed(() =>
  props.weeks.reduce((total, week) =>
    total + (week.actualDates || []).filter(date => isExceptionDay(date)).length, 0)
)
const monthSections = computed(() => {
  const sectionMap = new Map()

  for (const week of props.weeks) {
    const monthKeys = [...new Set((week.actualDates || []).map(monthKeyForDate))]

    for (const monthKey of monthKeys) {
      if (!sectionMap.has(monthKey)) {
        sectionMap.set(monthKey, {
          key: monthKey,
          label: monthLabelForKey(monthKey),
          weeks: []
        })
      }

      const cells = (week.cells || []).map(date => date && monthKeyForDate(date) === monthKey ? date : null)
      const actualDates = cells.filter(Boolean)
      if (actualDates.length) {
        sectionMap.get(monthKey).weeks.push({
          ...week,
          cells,
          actualDates,
          loadingKey: weekLoadingKey(week)
        })
      }
    }
  }

  return Array.from(sectionMap.values())
})

const renderedDateCellCount = computed(() =>
  monthSections.value.reduce((total, section) =>
    total + section.weeks.reduce((weekTotal, week) => weekTotal + week.cells.length, 0), 0)
)
const actualDateCount = computed(() =>
  props.weeks.reduce((total, week) => total + week.actualDates.length, 0)
)

function heatmapRenderDetails() {
  return {
    staffId: props.staff.id,
    staffName: `${props.staff.firstname || ''} ${props.staff.surname || ''}`.trim(),
    weekCount: props.weeks.length,
    monthSectionCount: monthSections.value.length,
    renderedDateCellCount: renderedDateCellCount.value,
    actualDateCount: actualDateCount.value,
    weeklyGaugeCount: props.showWeeklyGauges
      ? monthSections.value.reduce((total, section) => total + section.weeks.length, 0)
      : 0,
    isCollapsed: props.isCollapsed,
    hasBillableGauge: !props.isCollapsed,
    statCount: props.dailyStatsByStaff?.[props.staff.id]?.length || 0
  }
}

function logAfterPaint(label, startedAt) {
  nextTick(() => {
    requestAnimationFrame(() => {
      const durationMs = Math.round(performance.now() - startedAt)
      console.info(label, {
        durationMs,
        paintEstimateMs: durationMs,
        ...heatmapRenderDetails()
      })
    })
  })
}

onMounted(() => {
  logAfterPaint('[Staff Insights] heatmap card mounted', setupStartedAt)
})

onBeforeUpdate(() => {
  updateStartedAt = performance.now()
})

onUpdated(() => {
  if (!updateStartedAt) return
  const updateMs = Math.round(performance.now() - updateStartedAt)
  const details = heatmapRenderDetails()
  if (updateMs >= 8 || details.renderedDateCellCount >= 90) {
    console.info('[Staff Insights] heatmap card updated', {
      durationMs: updateMs,
      updateMs,
      ...details
    })
  }
  updateStartedAt = null
})

function statFor(date) {
  return statForDate(props.dailyStatsByStaff, props.staff.id, date, props.dailyStatsByStaffDateIndex)
}

function monthKeyForDate(date) {
  return date.slice(0, 7)
}

function monthLabelForKey(key) {
  return utcDateFromIso(`${key}-01`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

function tooltipText(date) {
  const stats = statFor(date)
  const hours = statHours(stats)
  const worked = hours.billable + hours.clientNonBillable + hours.internalNonBillable
  const pct = worked ? (hours.billable / worked) * 100 : 0
  const ptoText = hours.pto > 0 ? ` / ${formatCompactHours(hours.pto)} PTO` : ''
  const belowTargetText = isExceptionDay(date) ? ` / Below ${props.targetPct}% billable target` : ''
  return `${formatCompactHours(hours.billable)} billable / ${formatCompactHours(worked)} worked${ptoText} (${pct.toFixed(0)}%)${belowTargetText}`
}

function isNonWorkingDay(date) {
  const stats = statFor(date)
  const hours = statHours(stats)
  const worked = hours.billable + hours.clientNonBillable + hours.internalNonBillable
  return isWeekendIsoDate(date) && worked <= 0 && hours.pto <= 0
}

function isExceptionDay(date) {
  if (!isPastIsoDate(date)) return false
  if (isNonWorkingDay(date)) return false
  const stats = statFor(date)
  const hours = statHours(stats)
  const worked = hours.billable + hours.clientNonBillable + hours.internalNonBillable + hours.pto
  if (worked <= 0 && !isWeekendIsoDate(date)) return true
  if (worked <= 0) return false
  return ((hours.billable / worked) * 100) < props.targetPct
}

function cellStyle(date) {
  if (isNonWorkingDay(date)) return { backgroundColor: '#f3f4f6' }

  const baseColor = heatmapWorkedCellBackground(statFor(date), props.includeClientAsBillable)
  return { backgroundColor: baseColor }
}

function cellClass(date) {
  if (isExceptionDay(date)) return 'below-target-cell border-amber-300'
  if (isNonWorkingDay(date)) return 'border-gray-200'
  return 'border-gray-200'
}

function isWeekLoading(week) {
  const key = week.loadingKey || weekLoadingKey(week)
  return key ? !!props.loadingWeeks?.[key] : false
}

function weekSummary(week) {
  const stats = (week.actualDates || []).map(date => statFor(date))
  const totals = stats.reduce((acc, stat) => {
    const hours = statHours(stat)
    acc.billable += hours.billable
    acc.worked += hours.billable + hours.clientNonBillable + hours.internalNonBillable + hours.pto
    return acc
  }, { billable: 0, worked: 0 })
  const expected = adjustedExpectedHoursForDates(week.actualDates || [])
  return {
    ...totals,
    expected,
    delta: totals.worked - expected,
    billablePct: totals.worked ? Math.round((totals.billable / totals.worked) * 100) : 0
  }
}

function adjustedExpectedHoursForDates(dates) {
  return targetHoursForWeekdays(countWeekdaysInIsoDates(dates), weeklyTarget.value)
}

function formatCompactHours(value) {
  const parts = formatHoursToParts(value)
  return `${Number(parts.hours)}h ${parts.minutes}m`
}

function formatSignedHours(value) {
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${formatCompactHours(Math.abs(value))}`
}

function isEffectivelyZeroHours(value) {
  return Math.round(Math.abs(Number(value || 0)) * 60) === 0
}

function targetDeltaClass(value) {
  if (isEffectivelyZeroHours(value)) return 'text-gray-600'
  return value > 0 ? 'text-sushi' : 'text-red-600'
}

function targetDeltaLabel(value) {
  if (isEffectivelyZeroHours(value)) return 'On target'
  return value > 0 ? 'Over ' : 'Under '
}

function formatWeekTargetDelta(value) {
  if (isEffectivelyZeroHours(value)) return 'On target'
  return formatSignedHours(value)
}
</script>

<style scoped>
.below-target-cell {
  box-shadow:
    inset 0 0 0 1.5px rgba(217, 119, 6, 0.85),
    inset 0 0 10px rgba(217, 119, 6, 0.18);
}

.daily-target-control {
  margin-top: 1rem;
  margin-bottom: 0.75rem;
}

.billable-target-slider {
  height: 0.45rem;
  cursor: pointer;
  appearance: none;
  border-radius: 9999px;
  outline: none;
}

.billable-target-slider::-webkit-slider-runnable-track {
  height: 0.45rem;
  border-radius: 9999px;
}

.billable-target-slider::-webkit-slider-thumb {
  width: 0.9rem;
  height: 0.9rem;
  margin-top: -0.225rem;
  appearance: none;
  border: 2px solid #ffffff;
  border-radius: 9999px;
  background: #12a9c8;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.22);
}

.billable-target-slider::-moz-range-track {
  height: 0.45rem;
  border-radius: 9999px;
  background: transparent;
}

.billable-target-slider::-moz-range-thumb {
  width: 0.9rem;
  height: 0.9rem;
  border: 2px solid #ffffff;
  border-radius: 9999px;
  background: #12a9c8;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.22);
}
</style>
